import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Button, StatePanel } from "@anyseal/ui";
import { useAuth } from "./auth";
import { PortalShell } from "./PortalShell";
import { EmailVerificationPage, LoginPage, PasswordResetPage, RegisterPage } from "./pages/AuthPages";
import { OverviewPage } from "./pages/OverviewPage";
import { CredentialsPage } from "./pages/CredentialsPage";
import { ModelsPage } from "./pages/ModelsPage";
import { UsagePage } from "./pages/UsagePage";
import { BillingPage } from "./pages/BillingPage";
import { DocsPage } from "./pages/DocsPage";
import { SecurityPage } from "./pages/SecurityPage";
import { LegalPage } from "./pages/LegalPage";
import { GrowthPage } from "./pages/GrowthPage";
import { OAuthCompletePage, OAuthErrorPage } from "./pages/OAuthPages";
import { LandingPage } from "./pages/LandingPage";
import { PublicModelsPage } from "./pages/PublicModelsPage";
import { EnterprisePage } from "./pages/EnterprisePage";
import { useI18n } from "./i18n";

// Authenticated guard — anonymous users are sent to /login with the current
// path in state so a successful sign-in returns them where they started.
function Guard() {
  const { status } = useAuth();
  const { locale } = useI18n();
  const location = useLocation();
  if (status === "initializing") return <main className="portal-standalone-state"><StatePanel kind="loading" title={locale === "zh-CN" ? "正在恢复安全会话" : "Restoring your secure session"} description={locale === "zh-CN" ? "访问令牌仅保存在当前页面内存中。" : "The access token is kept only in this page's memory."} /></main>;
  if (status === "anonymous") return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <PortalShell />;
}

// Landing guard — shows the marketing home page to anonymous users; sends
// authenticated users straight to the dashboard.
function LandingGuard() {
  const { status } = useAuth();
  const { locale } = useI18n();
  if (status === "initializing") return <main className="portal-standalone-state"><StatePanel kind="loading" title={locale === "zh-CN" ? "正在恢复安全会话" : "Restoring your secure session"} /></main>;
  if (status === "authenticated") return <Navigate to="/dashboard" replace />;
  return <LandingPage />;
}

export function App() {
  return <Routes>    {/* ── Marketing pages (no auth required) ── */}
    <Route index element={<LandingGuard />} />
    <Route path="models" element={<PublicModelsPage />} />
    <Route path="enterprise" element={<EnterprisePage />} />
    <Route path="pricing" element={<Navigate to="/enterprise" replace />} />

    {/* ── Auth flow ── */}
    <Route path="/login" element={<LoginPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/password-reset" element={<PasswordResetPage />} />
    <Route path="/auth/password-reset" element={<PasswordResetPage />} />
    <Route path="/auth/email-verification" element={<EmailVerificationPage />} />
    <Route path="/auth/oauth/complete" element={<OAuthCompletePage />} />
    <Route path="/auth/oauth/error" element={<OAuthErrorPage />} />
    <Route path="/legal/public" element={<LegalPage publicView />} />

    {/* ── Authenticated portal (dashboard at /dashboard) ── */}
    <Route path="dashboard" element={<Guard />}>
      <Route index element={<OverviewPage />} />
      <Route path="credentials" element={<CredentialsPage />} />
      <Route path="models" element={<ModelsPage />} />
      <Route path="usage" element={<UsagePage />} />
      <Route path="billing" element={<BillingPage />} />
      <Route path="growth" element={<GrowthPage />} />
      <Route path="docs" element={<DocsPage />} />
      <Route path="security" element={<SecurityPage />} />
      <Route path="legal" element={<LegalPage />} />
    </Route>

    {/* Legacy root paths — redirect authenticated users to /dashboard equivalents */}
    <Route element={<Guard />}>
      <Route path="credentials" element={<Navigate to="/dashboard/credentials" replace />} />
      <Route path="usage" element={<Navigate to="/dashboard/usage" replace />} />
      <Route path="billing" element={<Navigate to="/dashboard/billing" replace />} />
      <Route path="growth" element={<Navigate to="/dashboard/growth" replace />} />
      <Route path="docs" element={<Navigate to="/dashboard/docs" replace />} />
      <Route path="security" element={<Navigate to="/dashboard/security" replace />} />
      <Route path="legal" element={<Navigate to="/dashboard/legal" replace />} />
    </Route>

    <Route path="*" element={<NotFoundPage />} />
  </Routes>;
}

function NotFoundPage() {
  const { locale } = useI18n();
  return (
    <main className="portal-standalone-state">
      <StatePanel
        kind="empty"
        title="404"
        description={locale === "zh-CN" ? "这个页面不存在。" : "This page does not exist."}
        action={<Button variant="secondary" onClick={() => window.history.back()}>{locale === "zh-CN" ? "返回" : "Back"}</Button>}
      />
    </main>
  );
}
